import React, { useContext, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import CleaningServicesOutlinedIcon from '@mui/icons-material/CleaningServicesOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import { VehicleContext } from '../SingleVehicle'
import AddVehicle from '../../../admin/siteConfiguration/AddVehicle'
import BigModal from '../../../../components/Modals/BigModal'
import AddNewService, {
  serviceModalStyle,
} from './ServicingMotInsurance/AddService'
import VehicleImage from './VehicleImage'
import Status from './Status'
import { StatTile, dateTone, formatDate } from './SectionKit'
import { LOLER_STATE } from './Loler/lolerOptions'

export const NumberPlate = ({ regPlate, size = 'medium' }) => (
  <Box
    display="inline-flex"
    alignItems="center"
    sx={{
      backgroundColor: '#FFD307',
      borderRadius: 1,
      border: '1px solid rgba(0,0,0,0.25)',
    }}
  >
    <Typography
      fontFamily="UKnumberplate"
      fontSize={size === 'medium' ? 18 : 14}
      paddingX={1}
      letterSpacing={2}
      lineHeight={size === 'medium' ? 1.6 : 1.5}
    >
      {regPlate}
    </Typography>
  </Box>
)

// Page header for a single vehicle: identity, live status, the primary
// record-keeping actions, and an at-a-glance compliance tile row (DVLA MOT
// and tax alongside AmbuNet's own service/clean/check records).
const VehicleHero = ({
  dvlaData,
  safetyChecksEnabled,
  onMarkUnavailable,
  onOpenSafety,
  unavailability = [],
  onOpenDowntime,
}) => {
  const navigate = useNavigate()
  const { vehicle, setVehicle, loadVehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const [moreAnchorEl, setMoreAnchorEl] = useState(null)
  const [editVehiclePopup, setEditVehiclePopup] = useState(false)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [openAddNewService, setOpenAddNewService] = useState(false)

  const handleArchive = () => {
    API.put('/fleet/' + vehicle._id, {
      archived: true,
      status: 'Archived',
    }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Vehicle Archived' })
        setVehicle((prev) => ({ ...prev, archived: true, status: 'Archived' }))
        setConfirmArchive(false)
      } else {
        setToast({
          message: 'Unable to archive this vehicle. Please try again later.',
        })
      }
    })
  }

  // Vehicles under 3 years old have no MOT yet and don't need one.
  const newVehicle =
    !dvlaData?.motExpiryDate &&
    dvlaData?.monthOfFirstRegistration &&
    moment(dvlaData.monthOfFirstRegistration, 'YYYY-MM').isAfter(
      moment().subtract(3, 'years'),
    )

  const loler = vehicle?.lolerStatus
  const tileCount = 5 + (safetyChecksEnabled ? 1 : 0) + (loler ? 1 : 0)

  const setupWarnings = [
    !vehicle?.makeReadyLoadList && 'Make Ready list not set',
    !vehicle?.baseLocation && 'Base location not set',
  ].filter(Boolean)

  // Planned downtime: a window running now, and planned work whose end has
  // passed without a service being recorded.
  const activeDowntime = unavailability.find(
    (period) =>
      moment().isAfter(period.start) && moment().isBefore(period.end),
  )
  const overdueDowntime = unavailability.filter(
    (period) =>
      period.unavailabilityType &&
      !period.completedAt &&
      moment().isAfter(period.end),
  )

  return (
    <Paper sx={{ p: 2, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box sx={{ width: { xs: '100%', md: 170 }, flexShrink: 0 }}>
            <VehicleImage
              imageKey={vehicle.image}
              vehicleID={vehicle._id}
              loadVehicle={loadVehicle}
              height={96}
            />
          </Box>
          <Box minWidth={0} flex={1}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="title2" fontSize={28} padding={0}>
                {vehicle.callSign}
              </Typography>
              <NumberPlate regPlate={vehicle.regPlate} />
              <Status vehicle={vehicle} setVehicle={setVehicle} />
              {vehicle.archived && (
                <Chip size="small" label="Archived" sx={{ borderRadius: 1 }} />
              )}
              {activeDowntime && (
                <Chip
                  size="small"
                  color="error"
                  icon={<EventBusyIcon />}
                  label={`Unavailable — ${
                    [
                      activeDowntime.unavailabilityType,
                      activeDowntime.unavailabilityReason,
                    ]
                      .filter(Boolean)
                      .join(': ') || 'downtime window'
                  }`}
                  onClick={onOpenDowntime}
                  sx={{ borderRadius: 1 }}
                />
              )}
              {overdueDowntime.length > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<WarningAmberIcon />}
                  label={`${overdueDowntime.length} planned ${
                    overdueDowntime.length === 1 ? 'service' : 'services'
                  } not recorded`}
                  onClick={onOpenDowntime}
                  sx={{ borderRadius: 1 }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {[
                vehicle?.vehicleType,
                vehicle?.baseLocation?.name,
                vehicle?.makeReadyLoadList?.title,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            {setupWarnings.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                mt={1}
                flexWrap="wrap"
                useFlexGap
              >
                {setupWarnings.map((warning) => (
                  <Chip
                    key={warning}
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<WarningAmberIcon />}
                    label={`${warning} — click to fix`}
                    onClick={() => setEditVehiclePopup(true)}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            sx={{ flexShrink: 0, maxWidth: { md: 340, lg: 'none' } }}
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={<ReportProblemOutlinedIcon />}
              onClick={() =>
                navigate('/report-defect?vehicleID=' + vehicle._id)
              }
            >
              Defect
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CleaningServicesOutlinedIcon />}
              onClick={() => navigate('/vehicle-clean?vehicle=' + vehicle._id)}
            >
              Clean
            </Button>
            {safetyChecksEnabled && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FactCheckOutlinedIcon />}
                onClick={() =>
                  navigate('/fleet/record-safety-check?vehicle=' + vehicle._id)
                }
              >
                Safety Check
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<BuildOutlinedIcon />}
              onClick={() => setOpenAddNewService(true)}
            >
              Service
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ChecklistOutlinedIcon />}
              onClick={() => navigate('/make-ready/' + vehicle._id)}
            >
              Make Ready
            </Button>
            <Button
              size="small"
              variant="outlined"
              endIcon={<MoreVertIcon />}
              onClick={(e) => setMoreAnchorEl(e.currentTarget)}
            >
              More
            </Button>
          </Stack>
        </Stack>

        <Box
          display="grid"
          gap={1.5}
          gridTemplateColumns={{
            xs: '1fr 1fr',
            sm: '1fr 1fr 1fr',
            lg: `repeat(${tileCount}, 1fr)`,
          }}
        >
          <StatTile
            label="MOT"
            value={
              newVehicle
                ? 'Not required yet'
                : formatDate(dvlaData?.motExpiryDate)
            }
            caption={
              dvlaData?.motExpiryDate || newVehicle ? 'DVLA' : 'No DVLA data'
            }
            tone={newVehicle ? 'success' : dateTone(dvlaData?.motExpiryDate)}
          />
          <StatTile
            label="Tax"
            value={formatDate(dvlaData?.taxDueDate)}
            caption={
              dvlaData?.taxStatus ??
              (dvlaData?.taxDueDate ? 'DVLA' : 'No DVLA data')
            }
            tone={dateTone(dvlaData?.taxDueDate)}
          />
          <StatTile
            label="Last Service"
            value={formatDate(vehicle?.lastServiceDate)}
          />
          <StatTile
            label="Last Clean"
            value={formatDate(vehicle?.lastClean?.end)}
            caption={
              vehicle?.lastDeepClean?.end
                ? `Deep clean ${formatDate(vehicle.lastDeepClean.end)}`
                : 'No deep clean recorded'
            }
          />
          {safetyChecksEnabled && (
            <StatTile
              label="Last Safety Check"
              value={formatDate(vehicle?.lastSafetyCheck?.testDate)}
            />
          )}
          {loler && (
            <Box
              onClick={onOpenSafety}
              sx={{ display: 'flex', cursor: 'pointer', minWidth: 0 }}
              title="Open LOLER on the Safety Checks tab"
            >
              <StatTile
                label="LOLER"
                value={
                  loler.state === 'missing'
                    ? 'No record'
                    : loler.state === 'failed'
                      ? 'Failed'
                      : formatDate(loler.nextDue)
                }
                caption={
                  loler.state === 'ok'
                    ? `Next exam · ${loler.equipment.length} item${loler.equipment.length === 1 ? '' : 's'}`
                    : LOLER_STATE[loler.state]?.label
                }
                tone={loler.tone}
              />
            </Box>
          )}
          <StatTile
            label="Active Defects"
            value={`${vehicle?.activeDefects ?? 0}`}
            tone={(vehicle?.activeDefects ?? 0) === 0 ? 'success' : 'error'}
          />
        </Box>
      </Stack>

      <Menu
        open={!!moreAnchorEl}
        onClose={() => setMoreAnchorEl(null)}
        anchorEl={moreAnchorEl}
      >
        <MenuItem
          onClick={() => {
            setEditVehiclePopup(true)
            setMoreAnchorEl(null)
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Vehicle</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onMarkUnavailable()
            setMoreAnchorEl(null)
          }}
        >
          <ListItemIcon>
            <EventBusyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Schedule Downtime</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConfirmArchive(true)
            setMoreAnchorEl(null)
          }}
        >
          <ListItemIcon>
            <ArchiveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive Vehicle</ListItemText>
        </MenuItem>
      </Menu>

      <AddVehicle
        open={editVehiclePopup}
        existingVehicle={vehicle}
        onClose={() => setEditVehiclePopup(false)}
        loadVehicle={loadVehicle}
      />

      <BigModal
        open={openAddNewService}
        onClose={() => setOpenAddNewService(false)}
        style={serviceModalStyle}
      >
        <AddNewService onClose={() => setOpenAddNewService(false)} />
      </BigModal>

      <Dialog open={confirmArchive} onClose={() => setConfirmArchive(false)}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Archive {vehicle.callSign}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            The vehicle will be hidden from day-to-day fleet lists. You can
            restore it later from Site Configuration → Fleet → Archived
            Vehicles.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmArchive(false)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleArchive}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default VehicleHero
